/**
 * Equipment catalog + vendor directory for the "Repair / Replace / Return" category.
 * Replicated verbatim from the approved client design (ticketing-portal-web-v32.html
 * `equipCatalog` / `EQUIP_VENDORS`). No backend catalog endpoint exists yet — this
 * static data is dispatched through a thunk (see submitTicketThunk.ts) so the
 * fetch/select/hook wiring is already in place to swap in a real GET later.
 */

export type EquipmentCaptureMode = "serial" | "quantity";
export type EquipmentVendorCode = "PEAK" | "DEMATIC";

export interface EquipmentVendor {
  code: EquipmentVendorCode;
  name: string;
  short: string;
  address: { line1: string; line2?: string; city: string; state: string; zip: string };
  email: string;
  phone: string;
}

export interface EquipmentCatalogItem {
  sku: string;
  desc: string;
  etype: string;
  vendor: EquipmentVendorCode;
  capture: EquipmentCaptureMode;
  active: boolean;
  photo?: string;
  generation?: number;
  sourcePlatform?: string;
  /** Serial number prefix used by Bulk Paste to match a pasted serial to this model. */
  serialPrefix?: string;
}

export const EQUIPMENT_VENDORS: Record<EquipmentVendorCode, EquipmentVendor> = {
  PEAK: {
    code: "PEAK",
    name: "Peak Technologies",
    short: "Peak",
    address: { line1: "2500 Broening Highway", line2: "Building 4, Receiving Dock 12", city: "Baltimore", state: "MD", zip: "21224" },
    email: "pepsico.returns@peaktech.com",
    phone: "(410) 555-0142",
  },
  DEMATIC: {
    code: "DEMATIC",
    name: "Dematic Contract Repair",
    short: "Dematic",
    address: { line1: "507 Prudential Road", line2: "Receiving Dock 4", city: "Horsham", state: "PA", zip: "19044" },
    email: "repair.na@dematic.com",
    phone: "(877) 555-0198",
  },
};

export const EQUIPMENT_CATALOG: EquipmentCatalogItem[] = [
  { sku: "LBDJ901291", desc: "TALKMAN A720X", etype: "Talkman", vendor: "DEMATIC", capture: "serial", active: true },
  { sku: "UNKNOWN-A720", desc: "TALKMAN A720", etype: "Talkman", vendor: "PEAK", capture: "serial", active: true },
  { sku: "UNKNOWN-A500", desc: "TALKMAN A500", etype: "Talkman", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ090225", desc: "BATTERY, TALKMAN A500/T5 HIGH CAPACITY", etype: "Battery", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ901288", desc: "BATTERY, A700 SERIES, HIGH CAPACITY", etype: "Battery", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ901207", desc: "BATTERY, SRX2/SRX3 HEADSET", etype: "Battery", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ902572", desc: "SRX3 ELECTRONICS MODULE Only", etype: "Headset", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ901280", desc: "BELT, TALKMANS A720 SERIES SIZE SMALL", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901279", desc: "BELT, TALKMANS A720 SERIES SIZE MEDIUM", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901278", desc: "BELT, TALKMANS A720 SERIES SIZE LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901277", desc: "BELT, TALKMANS A720 SERIES SIZE X-LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901276", desc: "BELT, TALKMANS A720 SERIES SIZE XX-LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901284", desc: "HOLSTER, A700 SERIES, STANDARD", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901286", desc: "CHARGER & PS, A700 SERIES, 6 TERMINAL W/ Mounting Bracket", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901285", desc: "CHARGER & PS, A700 SERIES, 12 BATTERY W/ Mounting Bracket", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090511", desc: "SRX2/SRX3 Battery Charger 20 Bay W. Mounting Bracket", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901325", desc: "MOUNTING BRACKET (A700 ONLY)", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ902005", desc: "VUQUEST 3320G-2 SCANNER ONLY (VUQUEST 3320G-2 SCANNER KIT BY HONEYWELL-support and treated as expense)", etype: "Scanner", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ090611", desc: "CABLE, Granit LONG RANGE SCANNER", etype: "Scanner Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090000", desc: "Mounting Disks, SRX2/C Headset (Bag of 20)", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090417", desc: "MICROPHONE CAPS, SRX2/SRX3 HEADSET BAG OF 20", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901355", desc: "EAR PAD FOAM FOR SRX2/SRX3 HEADSET(BAG OF 20)", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ900712", desc: "GLOVE,HAND FOR HONEYWELL SCANNER MODELS MK4225, 3310,3320", etype: "Scanner Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ902571", desc: "HEADBAND WITH STABILITY STRAP, SRX2 HEAD", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "X0250939", desc: "2100IXLR-3N Long Range Scanner + 3years support. Cable not included", etype: "Scanner", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ901229", desc: "CABLE, CUSTOM 48 INCH FOR 3320G SCANNER", etype: "Scanner Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090291", desc: "Mounting Disks, SR-20 Headset", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ900768", desc: "SR-20 TALKMAN LIGHTWEIGHT VOCOLLECT WIRED HEADSET", etype: "Headset", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ901085", desc: "BELT, TALKMAN(R) A500 SERIES SIZE SMALL", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901086", desc: "BELT, TALKMAN A500 SERIES SIZE MEDIUM", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901087", desc: "BELT, TALKMAN A500 SERIES SIZE LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901088", desc: "BELT, TALKMAN A500 SERIES SIZE X LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901089", desc: "BELT, TALKMAN A500 SERIES SIZE XX LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901090", desc: "BELT, TALKMAN A500 SERIES SIZE XXX LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901281", desc: "BELT, TALKMANS A720 SERIES SIZE X-SMALL", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901275", desc: "BELT, TALKMANS A720 SERIES SIZE XXX-LARGE", etype: "Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090232", desc: "CHARGER, A500, 10 BAY COMBINATION", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090233", desc: "MOUNTING BRACKET", etype: "Charging Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ902004", desc: "VUQUEST 3320G-2 SCANNER KIT BY HONEYWELL", etype: "Scanner", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ090609", desc: "Scanner, Granit 1980I Handscanner", etype: "Scanner", vendor: "PEAK", capture: "serial", active: true },
  { sku: "LBDJ900700", desc: "Foam Ear Pads, SR-20 Headset (Bag of 25)", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ900775", desc: "25 PACK WINDSCREENS VOCOLLECT SR20 #HD-700-102B", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ090143", desc: "LEATHERETTE EARPAD, SR-20 LIGHTWEIGHT HEADSET (BAG OF 25)", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ901322", desc: "STABILITY STRAPS, SRX2/SRX3 (BAG OF 20) for A720", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "LBDJ902009", desc: "VOCOLLECT SRX2/SRX3 T-BAR PADS (20PK)", etype: "Headset Accessory", vendor: "PEAK", capture: "quantity", active: true },
  { sku: "UNKNOWN-LRSCAN", desc: "LONG RANGE Yellow Handscanner", etype: "Scanner", vendor: "PEAK", capture: "serial", active: true },
  { sku: "UNKNOWN-CLIP", desc: "HEADSET CORD CLOTHING CLIP", etype: "Accessory", vendor: "PEAK", capture: "serial", active: true },
  { sku: "UNKNOWN-BELTCLIP", desc: "REPLACEMENT BELT CLIP FOR TALKMAN", etype: "Accessory", vendor: "PEAK", capture: "serial", active: true },
];

/**
 * SKU scope per sub-category — verbatim from the mock's `EQ_SCOPE` rules.
 * An RMA/Non-RMA ticket only lists the device types that path actually covers;
 * a Reclaim Return lists everything, since 100% of the floor must be accounted for.
 */
export function getEquipmentScopeForSubCategory(
  subCategory: string | undefined,
  catalog: EquipmentCatalogItem[] = EQUIPMENT_CATALOG
): EquipmentCatalogItem[] {
  const active = catalog.filter((item) => item.active);
  switch (subCategory) {
    case "RMA Repair or Replacement":
      return active.filter((item) => ["Talkman", "Headset", "Scanner", "Battery"].includes(item.etype));
    case "Non-RMA Replacement (End of Life)":
      return active.filter(
        (item) => (item.etype === "Talkman" && !item.desc.toUpperCase().includes("A720X")) || item.etype === "Charging Accessory"
      );
    case "Voice to Vision Reclaim Return":
      return active;
    default:
      return active;
  }
}
